import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../pages/landing-page/components/Footer';
import PersonalInfoStep from './components/PersonalInfoStep';
import MembershipSelectionStep from './components/MembershipSelectionStep';
import PaymentConfigurationStep from './components/PaymentConfigurationStep';
import VerificationStep from './components/VerificationStep';
import Icon from '../../components/AppIcon';
import { authService } from '../../services/authService';

const UserRegistration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    email: '',
    phone: '',
    idNumber: '',
    idType: 'CC',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    
    // Membership Selection
    selectedPlan: null,
    
    // Payment Configuration
    paymentMethod: '',
    paymentReceipt: null,
    transactionReference: '',
    
    // Verification
    emailVerified: false,
    verificationCode: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const steps = [
    { number: 1, title: 'Información Personal', icon: 'User' },
    { number: 2, title: 'Selección de Plan', icon: 'CreditCard' },
    { number: 3, title: 'Configuración de Pago', icon: 'Wallet' },
    { number: 4, title: 'Verificación', icon: 'CheckCircle' }
  ];

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleNext = () => {
    if (currentStep < steps?.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Step 1: Create Supabase Auth user
      console.log('Creating Supabase Auth user...');
      const authData = await authService?.signUp(
        formData?.email,
        formData?.password,
        {
          fullName: formData?.fullName,
          phone: formData?.phone,
          idNumber: formData?.idNumber,
          idType: formData?.idType,
          userType: 'user',
          membershipTier: formData?.selectedPlan?.name || 'Explorador',
          referralCode: formData?.referralCode || ''
        }
      );

      console.log('✅ Usuario creado en Supabase Auth:', authData?.user?.id);
      console.log('📧 Email:', formData?.email);
      console.log('🎫 Membership:', formData?.selectedPlan?.name);

      // Step 2: Navigate to login
      console.log('Registro completado exitosamente');
      navigate('/user-login', { 
        state: { 
          message: 'Registro exitoso. Por favor inicia sesión con tus credenciales.',
          email: formData?.email 
        } 
      });
    } catch (error) {
      console.error('❌ Error durante el registro:', error);
      setSubmitError(error?.message || 'Error al completar el registro. Por favor intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalInfoStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <MembershipSelectionStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 3:
        return (
          <PaymentConfigurationStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 4:
        return (
          <VerificationStep
            formData={formData}
            updateFormData={updateFormData}
            onSubmit={handleSubmit}
            onPrevious={handlePrevious}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted">
      <Header user={null} />
      <main className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-12">
            <div className="inline-block mb-4">
              <span className="bg-primary/10 text-primary px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wider">
                Registro de Usuario
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-foreground mb-4">
              Únete a BIZZNET
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Completa tu registro en 4 sencillos pasos y comienza a disfrutar de descuentos exclusivos
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="mb-12">
            <div className="flex items-center justify-between relative">
              {/* Progress Line */}
              <div className="absolute top-8 left-0 right-0 h-1 bg-gray-200 -z-10">
                <div
                  className="h-full bg-primary transition-all duration-500"
                  style={{ width: `${((currentStep - 1) / (steps?.length - 1)) * 100}%` }}
                />
              </div>

              {steps?.map((step) => (
                <div key={step?.number} className="flex flex-col items-center flex-1">
                  <div
                    className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
                      currentStep >= step?.number
                        ? 'bg-primary text-white shadow-lg transform scale-110'
                        : 'bg-white text-gray-400 border-2 border-gray-200'
                    }`}
                  >
                    {currentStep > step?.number ? (
                      <Icon name="Check" size={28} />
                    ) : (
                      <Icon name={step?.icon} size={28} />
                    )}
                  </div>
                  <div className="mt-3 text-center">
                    <p
                      className={`text-sm font-bold ${
                        currentStep >= step?.number ? 'text-primary' : 'text-gray-400'
                      }`}
                    >
                      Paso {step?.number}
                    </p>
                    <p
                      className={`text-xs mt-1 hidden sm:block ${
                        currentStep >= step?.number ? 'text-foreground' : 'text-muted-foreground'
                      }`}
                    >
                      {step?.title}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12">
            {renderStepContent()}
          </div>

          {/* Help Section */}
          <div className="mt-8 text-center bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
            <div className="flex items-center justify-center gap-3">
              <Icon name="HelpCircle" size={24} className="text-blue-600" />
              <p className="text-base text-blue-900 font-semibold">
                ¿Necesitas ayuda? Contactanos al +573152495959 o bizznet.latam@gmail.com
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UserRegistration;